import React, { useState, useRef } from "react"
import styled from "styled-components"
import { MobergIconSize } from "../../../../../../../Components/MobergIcon/MobergIcon"
import { MdErrorOutline, MdKeyboardArrowDown } from "react-icons/md"
import { MobergAnimationCurve, MobergAnimationTiming, MobergAnimationTimingMs } from "../../../../../../../Components/MobergAnimation/MobergAnimation"
import { MobergTheme, getColors } from "../../../../../../../Components/MobergThemes/MobergColors"
import { FloatingFocusManager, useDismiss, useFloating, useInteractions, useRole, shift, size, flip, autoUpdate, useTransitionStyles } from "@floating-ui/react"
import { MobergFontFamily, MobergFontSize } from "../../../../../../../Components/MobergFont/MobergFont"
import { PatientFile } from "../../../Types/PatientFile"
import { MobergRow } from "../../../../../../../Moberg"

interface PatientFileDropdownProps {
    options: PatientFile[]
    selectedValue: PatientFile | undefined
    onChange: (value: PatientFile) => void
    width?: number | string
    disabled?: boolean
    style?: React.CSSProperties
    isClinical?: boolean
}

interface PatientFileDropdownOption {
    patientFile: PatientFile
    fileName: string
    preloadInfo: string
}

export const PatientFileDropdown: React.FC<PatientFileDropdownProps> = ({ 
    options, 
    selectedValue, 
    onChange, 
    width, 
    disabled, 
    style,
    isClinical = false 
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Transform options to include fileName and preloadInfo
    const dropdownOptions: PatientFileDropdownOption[] = options.map(patientFile => {
        const fileName = patientFile.file_name || patientFile.obj_name || "";
        
        let preloadInfo = 'Unknown';
        if (patientFile.preload_status) {
            if (typeof patientFile.preload_status === 'string') {
                preloadInfo = patientFile.preload_status;
            } else if (patientFile.preload_status.status) {
                preloadInfo = patientFile.preload_status.status;
            }
        }

        return {
            patientFile,
            fileName,
            preloadInfo
        };
    });

    // Find selected option
    const selectedOption = dropdownOptions.find(option => {
        const selectedId = selectedValue?.id || selectedValue?.obj_id;
        const optionId = option.patientFile.id || option.patientFile.obj_id;
        return selectedId === optionId;
    });

    const { refs, floatingStyles, context } = useFloating({
        strategy: "fixed",
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: "bottom-start",
        middleware: [
            shift(),
            flip({
                fallbackAxisSideDirection: "start",
                padding: 8
            }),
            size({
                apply({ availableWidth, availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        maxWidth: `${availableWidth}px`,
                        maxHeight: `${availableHeight}px`,
                    })
                },
            }),
        ],
        whileElementsMounted: autoUpdate,
    })

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: MobergAnimationTimingMs.FAST,
    })

    const dismiss = useDismiss(context)
    const role = useRole(context)
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role])

    const handleOptionClick = (option: PatientFileDropdownOption) => {
        setIsOpen(false)
        if (option.patientFile.file_exists) {
            onChange(option.patientFile)
        }
    }

    // Menu item dimensions should match the dimensions of the dropdown box
    const referenceBoundingBox = refs.reference.current?.getBoundingClientRect()
    const menuItemDimensions = {
        height: Math.max(0, (referenceBoundingBox?.height ?? 0) - 2),
        width: Math.max(0, (referenceBoundingBox?.width ?? 0) - 2)
    }

    const FileNotFoundIndicator = () => (
        <MdErrorOutline size={MobergIconSize.REGULAR} color={getColors(MobergTheme.RED).main} />
    )

    const DropdownEndSlot = (({ option }: { option: PatientFileDropdownOption }) => {
        return option.patientFile.file_exists
            ? <PreloadInfo>{option.preloadInfo}</PreloadInfo>
            : (
                <MobergRow expand={false} horizontalAlign="center" style={{ width: "60px" }}>
                    <FileNotFoundIndicator />
                </MobergRow>
            )
    })

    const getTooltip = (option?: PatientFileDropdownOption) => {
        return option && !option.patientFile.file_exists 
            ? 'The files associated with this recording could not be found.' 
            : ''
    }

    return (
        <DropdownContainer ref={dropdownRef} style={{ 
            flex: width === '100%' ? 1 : '', 
            pointerEvents: disabled ? 'none' : 'auto', 
            opacity: disabled ? 0.7 : 1, 
            ...style 
        }}>
            <div style={{ position: "relative", display: 'flex', flex: 1 }}>
                <DropdownButton
                    ref={refs.setReference}
                    {...getReferenceProps()}
                    onMouseDown={() => setIsOpen(previous => !previous)}
                    style={{ width: width ?? "auto" }}
                    title={getTooltip(selectedOption)}
                >
                    <FileInfoSection>
                        {selectedOption && !selectedOption.patientFile.file_exists && <FileNotFoundIndicator />}
                        <FileName>{selectedOption?.fileName || "Select a patient file..."}</FileName>
                        {!isClinical && selectedOption && (
                            <>
                                <Divider />
                                <DropdownEndSlot option={selectedOption} />
                            </>
                        )}
                    </FileInfoSection>
                    <DropdownIcon isOpen={isOpen}>
                        <MdKeyboardArrowDown size={MobergIconSize.REGULAR} />
                    </DropdownIcon>
                </DropdownButton>
            </div>

            {isMounted && (
                <FloatingFocusManager context={context}>
                    <DropdownMenu ref={refs.setFloating} style={{ ...floatingStyles, ...transitionStyles }} {...getFloatingProps()}>
                        {dropdownOptions.map(option => (
                            <DropdownMenuItem
                                key={JSON.stringify(option.patientFile)}
                                patientFile={option.patientFile}
                                width={menuItemDimensions.width}
                                height={menuItemDimensions.height}
                                onMouseDown={event => { event.stopPropagation(); handleOptionClick(option) }}
                                title={getTooltip(option)}
                            >
                                <FileInfoSection>
                                    <FileName>{option.fileName}</FileName>
                                    {!isClinical && (
                                        <>
                                            <Divider />
                                            <DropdownEndSlot option={option} />
                                        </>
                                    )}
                                </FileInfoSection>
                                <IconSpacer />
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenu>
                </FloatingFocusManager>
            )}
        </DropdownContainer>
    )
}

const DropdownContainer = styled.div`
    display: flex;
    align-items: center;
    font-family: ${MobergFontFamily.REGULAR};
    font-size: ${MobergFontSize.REGULAR};
`

const DropdownButton = styled.button`
    position: relative;
    background: var(--color-input-bg);
    color: var(--color-text-primary);
    margin: 0;
    white-space: nowrap;
    border: 1px solid var(--color-border-default);
    display: flex;
    align-items: center;
    border-radius: 4px;
    padding: 12px 0px 12px 12px;
    transition: all ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};
    cursor: pointer;

    &:hover {
        background: var(--color-hover-neutral);
        border-color: var(--color-accent-interactive);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
        background: var(--color-hover-active-neutral);
    }

    &:focus {
        outline: none;
        border-color: var(--color-accent-interactive);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
`

const FileInfoSection = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: 8px;
`

const FileName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    flex: 1;
`

const Divider = styled.div`
    width: 1px;
    height: 20px;
    background: var(--color-border-default);
    margin: 0 16px;
    flex-shrink: 0;
    opacity: 0.6;
`

const PreloadInfo = styled.span`
    border: 1px solid ${getColors(MobergTheme.BLUE).main};
    color: ${getColors(MobergTheme.BLUE).main};
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
    width: 60px;
    text-align: center;
    background: rgba(59, 130, 246, 0.05);
    transition: all ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};
`

const DropdownIcon = styled.span<{ isOpen: boolean }>`
    display: flex;
    align-items: center;
    transform: rotate(${props => props.isOpen ? "-180deg" : "0"});
    transform-origin: center;
    transition: transform ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};
    padding-right: 12px;
    padding-left: 8px;
`

const IconSpacer = styled.div`
    width: 24px;
    flex-shrink: 0;
`

const DropdownMenu = styled.div`
    z-index: 10001;
    color: var(--color-text-primary);
    background: var(--color-surface-menu);
    border-radius: 4px;
    border: 1px solid var(--color-border-default);
    overflow: auto;
    overscroll-behavior: contain;
    box-shadow: var(--modal-box-shadow);
    max-height: 300px;
`

const DropdownMenuItem = styled.div<{ width: number; height: number, patientFile: PatientFile}>`
    padding: 8px 16px;
    display: flex;
    align-items: center;
    cursor: ${props => props.patientFile.file_exists ? 'pointer' : 'default'};
    white-space: nowrap;
    min-width: ${props => props.width}px;
    height: ${props => props.height}px;
    transition: all ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};
    ${props => !props.patientFile.file_exists && 'color: var(--text-secondary-color)'};

    &:hover {
        ${props => props.patientFile.file_exists && 'color: white'};
        background: ${props => props.patientFile.file_exists && 'var(--color-accent-interactive)'};
    }

    &:active {
        ${props => props.patientFile.file_exists && 'color: white'};
        background: ${props => props.patientFile.file_exists && 'var(--color-accent-interactive)'};
    }

    &:first-child {
        border-radius: 4px 4px 0 0;
    }

    &:last-child {
        border-radius: 0 0 4px 4px;
    }

    &:hover ${PreloadInfo} {
        background: var(--color-surface-panel);
        color: var(--color-accent-interactive);
        border-color: var(--color-surface-panel);
    }
` 